import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Checkbox, Group, Text, Stack, ActionIcon, Button, Divider, Box } from '@mantine/core';
import { IconGripVertical } from '@tabler/icons-react';
import type { ColumnKey } from '@/hooks/useColumnPreferences';

interface ColumnOption {
  key: ColumnKey;
  label: string;
}

interface DraggableColumnListProps {
  columns: ColumnKey[];
  allColumns: ColumnOption[];
  onToggle: (key: ColumnKey) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
}

export function DraggableColumnList({
  columns,
  allColumns,
  onToggle,
  onReorder,
  onReset,
}: DraggableColumnListProps) {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    onReorder(result.source.index, result.destination.index);
  };

  // Split columns into visible (in order) and hidden
  const visibleColumns = columns
    .map(key => allColumns.find(col => col.key === key))
    .filter((col): col is ColumnOption => col !== undefined);

  const hiddenColumns = allColumns.filter(col => !columns.includes(col.key));

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>Visible Columns (drag to reorder)</Text>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="columns">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {visibleColumns.map((col, index) => (
                <Draggable key={col.key} draggableId={col.key} index={index}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        marginBottom: 4,
                        padding: '4px 0',
                        backgroundColor: snapshot.isDragging ? 'var(--mantine-color-gray-1)' : undefined,
                        borderRadius: 4,
                      }}
                    >
                      <Group gap="xs" wrap="nowrap">
                        <ActionIcon
                          {...provided.dragHandleProps}
                          variant="subtle"
                          color="gray"
                          size="sm"
                          style={{ cursor: 'grab' }}
                          data-testid="drag-handle"
                          aria-label="Drag to reorder"
                        >
                          <IconGripVertical size={14} />
                        </ActionIcon>
                        <Checkbox
                          label={col.label}
                          checked={true}
                          onChange={() => onToggle(col.key)}
                          disabled={col.key === 'name'}
                          styles={{ root: { flex: 1 } }}
                        />
                      </Group>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {hiddenColumns.length > 0 && (
        <>
          <Divider my="xs" />
          <Text size="sm" fw={500} c="dimmed">Hidden Columns</Text>
          {hiddenColumns.map(col => (
            <Group key={col.key} gap="xs" wrap="nowrap" pl={30}>
              <Checkbox
                label={col.label}
                checked={false}
                onChange={() => onToggle(col.key)}
              />
            </Group>
          ))}
        </>
      )}

      <Divider my="xs" />
      <Button variant="subtle" size="xs" onClick={onReset} aria-label="Reset to defaults">
        Reset to defaults
      </Button>
    </Stack>
  );
}
